/**
 * Modal de crediário no caixa: número de parcelas manual, vencimentos editáveis.
 * Padrão: 1 parcela, vencimento em 30 dias, valor total.
 */

import { useState, useEffect } from 'react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { CalendarClock, Minus, Plus } from 'lucide-react'
import './CrediarioPaymentModal.css'

const DAYS_UNTIL_FIRST = 30
const DAYS_BETWEEN = 30
const MIN_PARCELS = 1
const MAX_PARCELS = 120

const formatDateISO = (date) => {
  const d = new Date(date)
  d.setHours(12, 0, 0, 0)
  return d.toISOString().split('T')[0]
}

/** Divide o valor em parcelas iguais (centavos na última parcela). */
export function buildCrediarioInstallments(amount, count, previousInstallments = []) {
  const safeCount = Math.max(MIN_PARCELS, Math.min(MAX_PARCELS, count || 1))
  const totalCents = Math.round((parseFloat(amount) || 0) * 100)
  const baseCents = Math.floor(totalCents / safeCount)
  const remainder = totalCents - baseCents * safeCount

  const baseDate = new Date()
  baseDate.setHours(12, 0, 0, 0)

  const list = []
  for (let i = 0; i < safeCount; i++) {
    const prev = previousInstallments.find((p) => p.installmentNumber === i + 1)

    let dueDate
    if (prev?.dueDate) {
      dueDate = prev.dueDate
    } else {
      const due = new Date(baseDate)
      due.setDate(due.getDate() + DAYS_UNTIL_FIRST + i * DAYS_BETWEEN)
      dueDate = formatDateISO(due)
    }

    const installmentCents = baseCents + (i === safeCount - 1 ? remainder : 0)

    list.push({
      installmentNumber: i + 1,
      dueDate,
      amount: installmentCents / 100
    })
  }

  return list
}

function CrediarioPaymentModal({ isOpen, onClose, amount, onConfirm }) {
  const [parcelCount, setParcelCount] = useState(1)
  const [installments, setInstallments] = useState([])
  const [validationError, setValidationError] = useState('')

  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0)

  useEffect(() => {
    if (!isOpen) return
    setParcelCount(1)
    setValidationError('')
    setInstallments(buildCrediarioInstallments(amount, 1, []))
  }, [isOpen, amount])

  const applyParcelCount = (next) => {
    const clamped = Math.max(MIN_PARCELS, Math.min(MAX_PARCELS, next))
    setParcelCount(clamped)
    setInstallments((prev) => buildCrediarioInstallments(amount, clamped, prev))
    setValidationError('')
  }

  const handleParcelInputChange = (e) => {
    const raw = e.target.value
    if (raw === '') {
      setParcelCount('')
      return
    }
    const parsed = parseInt(raw, 10)
    if (!Number.isNaN(parsed)) {
      applyParcelCount(parsed)
    }
  }

  const handleParcelInputBlur = () => {
    if (parcelCount === '' || parcelCount < MIN_PARCELS) {
      applyParcelCount(1)
    }
  }

  const handleUpdateInstallmentDate = (installmentNumber, date) => {
    setInstallments((prev) =>
      prev.map((inst) =>
        inst.installmentNumber === installmentNumber ? { ...inst, dueDate: date } : inst
      )
    )
    setValidationError('')
  }

  const installmentsTotal = installments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0)
  const amountNum = parseFloat(amount) || 0
  const totalsMatch = Math.abs(installmentsTotal - amountNum) < 0.02

  const handleConfirm = () => {
    if (installments.length === 0) {
      setValidationError('Informe ao menos uma parcela.')
      return
    }

    const missingDate = installments.some((inst) => !inst.dueDate)
    if (missingDate) {
      setValidationError('Preencha a data de vencimento de todas as parcelas.')
      return
    }

    if (!totalsMatch) {
      setValidationError('A soma das parcelas deve ser igual ao valor do crediário.')
      return
    }

    onConfirm({
      paymentTermId: null,
      installments: installments.map((inst) => ({
        installmentNumber: inst.installmentNumber,
        dueDate: inst.dueDate,
        amount: parseFloat(inst.amount) || 0
      }))
    })
  }

  const displayParcelCount = parcelCount === '' ? '' : parcelCount

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Crediário — parcelas"
      icon={<CalendarClock size={24} />}
      size="medium"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleConfirm}
            disabled={installments.length === 0}
          >
            Confirmar
          </button>
        </>
      }
    >
      <div className="crediario-modal-summary">
        Valor do crediário: <strong>{formatCurrency(amount)}</strong>
      </div>

      <div className="crediario-modal-parcelas-row">
        <div className="crediario-modal-parcelas-field">
          <label htmlFor="crediario-parcel-count">Número de parcelas</label>
          <div className="crediario-modal-parcelas-input-wrap">
            <button
              type="button"
              className="crediario-modal-parcelas-btn"
              aria-label="Menos uma parcela"
              disabled={(parcelCount || 1) <= MIN_PARCELS}
              onClick={() => applyParcelCount((parcelCount || 1) - 1)}
            >
              <Minus size={18} />
            </button>
            <input
              id="crediario-parcel-count"
              type="number"
              min={MIN_PARCELS}
              max={MAX_PARCELS}
              step={1}
              value={displayParcelCount}
              onChange={handleParcelInputChange}
              onBlur={handleParcelInputBlur}
              onWheel={(e) => e.currentTarget.blur()}
              aria-label="Número de parcelas"
            />
            <button
              type="button"
              className="crediario-modal-parcelas-btn"
              aria-label="Mais uma parcela"
              disabled={(parcelCount || 1) >= MAX_PARCELS}
              onClick={() => applyParcelCount((parcelCount || 1) + 1)}
            >
              <Plus size={18} />
            </button>
          </div>
        </div>
        <p className="crediario-modal-hint">
          Padrão: 1 parcela com vencimento em {DAYS_UNTIL_FIRST} dias. Novas parcelas são espaçadas de{' '}
          {DAYS_BETWEEN} em {DAYS_BETWEEN} dias (datas podem ser alteradas abaixo).
        </p>
      </div>

      {installments.length > 0 && (
        <>
          <p className="crediario-modal-installments-title">Vencimentos e valores</p>
          <div className="crediario-modal-installments-list">
            {installments.map((inst) => (
              <div key={inst.installmentNumber} className="crediario-modal-installment-item">
                <span className="crediario-modal-installment-number">
                  Parcela {inst.installmentNumber}/{installments.length}
                </span>
                <input
                  type="date"
                  className="crediario-modal-installment-date"
                  value={inst.dueDate || ''}
                  onChange={(e) =>
                    handleUpdateInstallmentDate(inst.installmentNumber, e.target.value)
                  }
                  aria-label={`Vencimento parcela ${inst.installmentNumber}`}
                />
                <span className="crediario-modal-installment-amount">
                  {formatCurrency(inst.amount)}
                </span>
              </div>
            ))}
          </div>

          <div className={`crediario-modal-total-check ${!totalsMatch ? 'mismatch' : ''}`}>
            <span>Soma das parcelas</span>
            <strong>{formatCurrency(installmentsTotal)}</strong>
          </div>
        </>
      )}

      {validationError && <p className="crediario-modal-error">{validationError}</p>}
    </Modal>
  )
}

export default CrediarioPaymentModal
